import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { billingApi, productsApi } from '../services/api';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { toast } from 'sonner';

export function useBilling() {
  const queryClient = useQueryClient();
  const { user, loading: authLoading } = useAuth();

  // Get current subscription
  const useSubscription = () => {
    return useQuery({
      queryKey: ['billing', 'subscription'],
      queryFn: billingApi.getSubscription,
      enabled: !authLoading && !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get usage statistics
  const useUsage = (params?: {
    startDate?: string;
    endDate?: string;
  }) => {
    return useQuery({
      queryKey: ['billing', 'usage', params],
      queryFn: () => billingApi.getUsage(params),
      enabled: !authLoading && !!user,
      staleTime: 5 * 60 * 1000, // 5 minutes
    });
  };

  // Get invoices
  const useInvoices = (params?: {
    limit?: number;
    startingAfter?: string;
  }) => {
    return useQuery({
      queryKey: ['billing', 'invoices', params],
      queryFn: () => billingApi.getInvoices(params),
      enabled: !authLoading && !!user,
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  // Download invoice mutation
  const downloadInvoiceMutation = useMutation({
    mutationFn: billingApi.downloadInvoice,
    onSuccess: (data) => {
      // Open invoice PDF in new tab
      if (data.downloadUrl) {
        window.open(data.downloadUrl, '_blank');
        toast.success('Invoice download started');
      } else {
        toast.error('Invoice PDF not available');
      }
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to download invoice';
      toast.error(errorMessage);
    },
  });

  // Sync subscription data from Stripe
  const syncSubscriptionMutation = useMutation({
    mutationFn: billingApi.syncSubscription,
    onSuccess: () => {
      // Invalidate all billing-related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['billing'] });
      toast.success('Subscription data synced successfully');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.error || 'Failed to sync subscription data';
      toast.error(errorMessage);
    },
  });

  // Create checkout session mutation
  const createCheckoutSessionMutation = useMutation({
    mutationFn: billingApi.createCheckoutSession,
    onSuccess: (data) => {
      // Redirect to Stripe checkout
      window.location.href = data.sessionUrl;
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create checkout session');
    },
  });

  // Enhanced portal session with sync on return
  const createPortalSessionMutation = useMutation({
    mutationFn: billingApi.createPortalSession,
    onSuccess: (data) => {
      // Store a flag to sync data when user returns
      localStorage.setItem('billing-portal-visited', Date.now().toString());
      
      // Redirect to Stripe customer portal
      window.location.href = data.sessionUrl;
    },
    onError: (error: any) => {
      console.error('Portal session error:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to access billing portal';
      toast.error(errorMessage);
    },
  });

  // Function to handle return from billing portal
  const handlePortalReturn = () => {
    const portalVisited = localStorage.getItem('billing-portal-visited');
    if (portalVisited) {
      const visitTime = parseInt(portalVisited);
      const now = Date.now();
      
      // If visited within the last hour, sync data
      if (now - visitTime < 60 * 60 * 1000) {
        localStorage.removeItem('billing-portal-visited');
        syncSubscriptionMutation.mutate();
        return true; // Indicates sync was triggered
      }
    }
    return false;
  };

  // Get available products
  const useProducts = () => {
    return useQuery({
      queryKey: ['products'],
      queryFn: productsApi.getProducts,
      staleTime: 10 * 60 * 1000, // 10 minutes
    });
  };

  return {
    useSubscription,
    useUsage,
    useInvoices,
    useProducts,
    createCheckoutSession: createCheckoutSessionMutation.mutateAsync,
    createPortalSession: createPortalSessionMutation.mutateAsync,
    syncSubscription: syncSubscriptionMutation.mutateAsync,
    downloadInvoice: downloadInvoiceMutation.mutateAsync,
    handlePortalReturn,
    isCreatingCheckout: createCheckoutSessionMutation.isPending,
    isCreatingPortal: createPortalSessionMutation.isPending,
    isSyncing: syncSubscriptionMutation.isPending,
    isDownloadingInvoice: downloadInvoiceMutation.isPending,
  };
}